import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { ProviderDialog } from './ProviderDialog';
import { useDeleteProvider, useProviders, useTestProvider } from './queries';
import type { BackupProvider } from './api';

/**
 * Tokenmanagement -- die Zugaenge zu fremden Diensten.
 *
 * **Warum diese Maske getrennt von "Zugangstoken" steht.** Sie sehen
 * benachbart aus und sind das Gegenteil voneinander: Unter "Zugangstoken"
 * gibt **dieses System** einer Maschine eine Vollmacht; hier hinterlegt
 * jemand die Zugangsdaten, mit denen **dieses System** sich bei einem
 * fremden Dienst anmeldet. Die eine Liste wird widerrufen, die andere
 * gepflegt. Sie in einer Maske zusammenzulegen hiesse, zwei
 * Vertrauensrichtungen in einer Tabelle zu fuehren -- und irgendwann
 * widerruft jemand das Falsche.
 *
 * **Das Kennwort erscheint nie wieder.** Es wird verschluesselt abgelegt und
 * dem Anbieter im Klartext vorgelegt; ein Hash liesse sich dafuer nicht
 * zurueckrechnen. Wer es vergisst, traegt ein neues ein -- dieselbe Regel
 * wie beim Postfachkennwort.
 *
 * **Die Verbindungspruefung geht bis zur Statistikabfrage.** Anmeldung und
 * Partnerbaum gelingen auch dann, wenn die Statistik anschliessend mit
 * Fehler 13501 abgelehnt wird. Eine Pruefung, die vorher aufhoert, meldet
 * "in Ordnung" fuer einen Zugang, der nichts liefert.
 */
export function TokenManagementPage() {
  const { t, i18n } = useTranslation();

  const providers = useProviders();
  const remove = useDeleteProvider();
  const check = useTestProvider();

  const [editing, setEditing] = useState<BackupProvider | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title={t('tokens.title')}
        subtitle={t('tokens.subtitle')}
        action={
          <Button
            onClick={() => {
              setCreating(true);
            }}
          >
            {t('tokens.add')}
          </Button>
        }
      />

      {check.data ? (
        <section className="card">
          <Badge tone={check.data.ok ? 'success' : 'danger'}>
            {check.data.ok ? t('tokens.testOk') : t('tokens.testFailed')}
          </Badge>{' '}
          {check.data.message}
          {check.data.ok ? (
            <p className="table__muted">
              {t('tokens.testDetail', {
                partners: check.data.partners ?? 0,
                devices: check.data.devices ?? 0,
              })}
            </p>
          ) : null}
          {check.data.provider_code ? (
            <p className="table__muted">
              {t('tokens.providerCode', { code: check.data.provider_code })}
            </p>
          ) : null}
        </section>
      ) : null}

      {check.isError ? <ErrorNotice error={check.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <section className="card card--flush">
        {providers.isPending ? <SkeletonTable columns={5} /> : null}
        {providers.isError ? <ErrorNotice error={providers.error} /> : null}

        {providers.data?.length === 0 ? (
          <EmptyState
            icon="tokens"
            title={t('tokens.empty')}
            hint={t('tokens.emptyHint')}
            action={
              <Button
                onClick={() => {
                  setCreating(true);
                }}
              >
                {t('tokens.add')}
              </Button>
            }
          />
        ) : null}

        {providers.data && providers.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table table--sticky-actions">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('tokens.apiUser')}</th>
                  <th scope="col">{t('tokens.partnerId')}</th>
                  <th scope="col">{t('tokens.lastCheck')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('app.save')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {providers.data.map((provider) => (
                  <tr key={provider.id}>
                    <td>
                      <span className="table__subject">{provider.name}</span>
                      {!provider.is_active ? (
                        <>
                          {' '}
                          <Badge tone="neutral">{t('tokens.inactive')}</Badge>
                        </>
                      ) : null}
                      <div className="table__muted">{provider.base_url}</div>
                    </td>
                    <td className="table__muted">{provider.api_user}</td>
                    <td className="table__muted">
                      {provider.partner_name ? `${provider.partner_name} · ` : ''}
                      {provider.partner_id}
                    </td>
                    <td className="table__muted">
                      {provider.last_check_at
                        ? new Date(provider.last_check_at).toLocaleString(i18n.language)
                        : t('tokens.neverChecked')}
                      {/* Der letzte Fehler bleibt stehen, bis ein Abruf
                          gelingt. Ohne diesen Vermerk faellt ein abgelaufenes
                          Kennwort wochenlang niemandem auf -- es kommen
                          einfach keine Geraete mehr. */}
                      {provider.last_error ? (
                        <div>
                          <Badge tone="danger">{provider.last_error}</Badge>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row">
                        <Button
                          variant="secondary"
                          disabled={check.isPending}
                          onClick={() => {
                            check.mutate(provider.id);
                          }}
                        >
                          {check.isPending ? t('tokens.testing') : t('tokens.test')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditing(provider);
                          }}
                        >
                          {t('masterdata.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            if (
                              window.confirm(t('tokens.confirmDelete', { name: provider.name }))
                            ) {
                              remove.mutate(provider.id);
                            }
                          }}
                        >
                          {t('app.remove')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {creating || editing ? (
        <ProviderDialog
          provider={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}
